/**
 * Subcategory hub page — route: `/:industryId/:slug` (e.g. `/legal/land-property-law`).
 *
 * Data flow:
 *   - Overview tab → static mock (`utils/mockOverviewData.js` + `data/subcategoryImages.js`)
 *   - Articles / Media tabs → API when user has topic access
 *   - Provider sidebar → API with mock sponsor fallback
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import { useSelector } from "react-redux";
import {
  ArrowRight,
  ChevronRight,
  Loader2,
  MessageCircle,
} from "lucide-react";
import {
  BETTING_GAMING_MENU_SUMMARIES,
  CAREERS_MENU_SUMMARIES,
  HEALTH_MENU_SUMMARIES,
  INDUSTRIES,
  INDUSTRY_MENU_CATEGORIES,
  LEGAL_MENU_SUMMARIES,
  NUTRITION_MENU_SUMMARIES,
  REAL_ESTATE_MENU_SUMMARIES,
  getItemsByIndustry,
} from "../../data/categoryData";
import { useCategories } from "../../contexts/CategoriesContext";
import { getLegalHubVisuals } from "../../data/legalHubVisuals";
import Footer from "../../components/footer/Footer";
import { useSignupCta } from "../../hooks/useSignupCta";
import { getSponsorForIndustry } from "../../data/mockContentData";
import { buildSubcategoryOverview } from "../../utils/mockOverviewData";
import {
  fadeUp,
  foregroundOnSolidAccent,
  GenericOverviewTab,
  heroAccentStatColor,
  importIcon,
  LegalHubVisualContext,
  ProviderHeroCard,
  ProviderSidebarCard,
  StickyTabBar,
  tabFade,
} from "./subcategory_pages/shared/categoryDetailShared";
import { resolveCanonicalTopicSlug, topicSlugsMatch } from "../../utils/topicSlugAliases";
import { useSubcategoryInterestGate } from "../../hooks/useSubcategoryInterestGate";
import { useEngagementSubscribePrompt } from "../../hooks/useEngagementSubscribePrompt";
import SubcategoryInterestGateModal from "../../components/guest/SubcategoryInterestGateModal";
import TopicMediaTab from "../../components/media/TopicMediaTab";
import TopicArticlesTab from "../../components/articles/TopicArticlesTab";
import ArticleAuthFlowModal from "../../components/auth/ArticleAuthFlowModal";
import { useTopicMedia } from "../../hooks/useTopicMedia";
import { useTopicArticles } from "../../hooks/useTopicArticles";
import { useTopicProvider } from "../../hooks/useTopicProvider";
import { buildExpertWhatsappUrl } from "../../utils/providerDisplay";
import { resolveTopicBackendIds } from "../../utils/resolveTopicBackendIds";
import { isTopicContentAccessGranted } from "../../utils/topicAccess";

const SUMMARIES_BY_INDUSTRY = {
  "real-estate": REAL_ESTATE_MENU_SUMMARIES,
  legal: LEGAL_MENU_SUMMARIES,
  nutrition: NUTRITION_MENU_SUMMARIES,
  health: HEALTH_MENU_SUMMARIES,
  careers: CAREERS_MENU_SUMMARIES,
  "betting-gaming": BETTING_GAMING_MENU_SUMMARIES,
};

/** Aligns with `INDUSTRY_MENU_CATEGORIES` ids (`leg-1`, …) for menu summary lookup. */
const STATIC_MENU_ID_PREFIX = {
  "real-estate": "re",
  legal: "leg",
  nutrition: "nut",
  health: "hlth",
  careers: "car",
  "betting-gaming": "bet",
};

const EMPTY_MENU = [];

function pickMenuSlug(entry) {
  if (!entry) return "";
  if (entry.slug) return String(entry.slug);
  if (typeof entry.path === "string") {
    return entry.path.split("/").filter(Boolean).pop() || "";
  }
  return "";
}

function menuItemFromRegistryEntry(industryId, entry) {
  if (!entry) return null;
  const prefix = STATIC_MENU_ID_PREFIX[industryId] || "sub";
  return {
    id: `${prefix}-${entry.id}`,
    label: entry.name,
    path: `/${industryId}/${entry.slug}`,
    description: entry.description,
    icon: entry.icon,
    color: entry.color,
    slug: entry.slug,
  };
}

/** Matches `lg:top-[calc(4.375rem+3.875rem)]`: GuestNavbar `sm:h-[70px]` + tab bar band (~3.875rem). */
const ASIDE_STICKY_TOP_PX = 70 + 3.875 * 16;

const CategoryDetailPage = () => {
  const { industryId: paramIndustryId, slug: slugParam } = useParams();
  const location = useLocation();
  const { to: ctaTo } = useSignupCta();
  const isAuthenticated = useSelector((s) => Boolean(s.infoboxx_consumer?.token));
  const user = useSelector((s) => s.infoboxx_consumer?.user);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = location.state?.activeTab;
    return tab === "articles" || tab === "media" || tab === "overview" ? tab : "overview";
  });
  const asideStickyRef = useRef(null);
  const [railPinned, setRailPinned] = useState(false);
  const [whatsappPending, setWhatsappPending] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authIntent, setAuthIntent] = useState("signup");
  const interestGateWasOpenRef = useRef(false);
  const guestPromptConsumedRef = useRef(false);
  const {
    loading: categoriesLoading,
    industries: apiIndustries,
    menuByIndustry: apiMenuByIndustry,
    signupCategories,
  } = useCategories();
  const industryId = paramIndustryId ?? location.pathname.split("/").filter(Boolean)[0];
  const slug = useMemo(
    () => resolveCanonicalTopicSlug(industryId, slugParam),
    [industryId, slugParam],
  );

  const scrollToOverviewContent = useCallback(() => {
    setActiveTab("overview");
    window.setTimeout(() => {
      document.getElementById("topic-content")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  }, []);

  const scrollToTopicContent = useCallback(() => {
    window.setTimeout(() => {
      document.getElementById("topic-content")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 80);
  }, []);

  const industries = apiIndustries.length ? apiIndustries : INDUSTRIES;
  /** URL segments stay stable (`/legal/...`); API may omit or rename an industry — fall back to static INDUSTRIES. */
  const industry =
    industries.find((ind) => ind.id === industryId) ??
    INDUSTRIES.find((ind) => ind.id === industryId) ??
    industries.find(
      (ind) => String(ind.path || "").replace(/^\//, "") === industryId,
    ) ??
    null;
  const apiMenu = useMemo(
    () => apiMenuByIndustry[industryId] ?? EMPTY_MENU,
    [apiMenuByIndustry, industryId],
  );
  const staticMenu = useMemo(
    () => INDUSTRY_MENU_CATEGORIES[industryId] ?? EMPTY_MENU,
    [industryId],
  );
  const menuCategories = useMemo(
    () => (apiMenu.length ? apiMenu : staticMenu),
    [apiMenu, staticMenu],
  );
  const menuItem = useMemo(() => {
    const raw = slug ? decodeURIComponent(slug) : "";
    const target = (raw || slug || "").trim();
    if (!target) return null;

    const match = (list) =>
      list.find((c) => {
        const s = pickMenuSlug(c);
        return (
          s === target ||
          s === slug ||
          topicSlugsMatch(s, target) ||
          topicSlugsMatch(s, slugParam ?? "")
        );
      });

    const registryHit = getItemsByIndustry(industryId).find(
      (it) => topicSlugsMatch(it.slug, target) || topicSlugsMatch(it.slug, slugParam ?? ""),
    );

    return (
      match(apiMenu) ??
      match(staticMenu) ??
      (registryHit ? menuItemFromRegistryEntry(industryId, registryHit) : null)
    );
  }, [apiMenu, staticMenu, slug, slugParam, industryId]);

  const interestGateAccent = menuItem?.color ?? industry?.color ?? "#c9a000";

  const interestGate = useSubcategoryInterestGate({
    isAuthenticated,
    user,
    signupCategories,
    industryId,
    industryLabel: industry?.label ?? "",
    menuItem,
    slug,
  });
  const { open: interestGateOpen, setOpen: setInterestGateOpen } = interestGate;

  const topicIds = useMemo(
    () => resolveTopicBackendIds(signupCategories, industryId, menuItem),
    [signupCategories, industryId, menuItem],
  );

  const canReadTopicContent = isTopicContentAccessGranted(
    isAuthenticated,
    user,
    topicIds,
  );

  useEffect(() => {
    guestPromptConsumedRef.current = false;
  }, [industryId, slug]);

  const guestEngagement = useEngagementSubscribePrompt({
    enabled: !isAuthenticated && Boolean(menuItem),
    requireScroll: false,
    persistDismiss: false,
  });

  useEffect(() => {
    if (!guestEngagement.shouldOpen || guestPromptConsumedRef.current || authModalOpen) return;
    setAuthIntent("signup");
    setAuthModalOpen(true);
  }, [guestEngagement.shouldOpen, authModalOpen]);

  const openSignupModal = useCallback(() => {
    setAuthIntent("signup");
    setAuthModalOpen(true);
  }, []);

  const topicArticles = useTopicArticles({
    signupCategories,
    industryId,
    menuItem,
    enabled: Boolean(menuItem) && canReadTopicContent,
  });

  const topicMedia = useTopicMedia({
    isAuthenticated,
    user,
    signupCategories,
    industryId,
    menuItem,
    enabled: Boolean(menuItem),
  });

  const topicProvider = useTopicProvider({
    isAuthenticated,
    user,
    signupCategories,
    industryId,
    menuItem,
    enabled: Boolean(menuItem),
  });

  const sponsorForContact = useMemo(() => getSponsorForIndustry(industryId), [industryId]);
  const expertWhatsappUrl = useMemo(
    () =>
      buildExpertWhatsappUrl({
        provider: topicProvider.provider,
        isAuthenticated,
        fallbackSponsor: sponsorForContact,
      }),
    [topicProvider.provider, isAuthenticated, sponsorForContact],
  );

  const openExpertWhatsapp = useCallback(() => {
    if (!expertWhatsappUrl || typeof window === "undefined") return;
    window.open(expertWhatsappUrl, "_blank", "noopener,noreferrer");
  }, [expertWhatsappUrl]);

  const handleTalkToExpert = useCallback(() => {
    if (!expertWhatsappUrl) return;

    if (!isAuthenticated) {
      setWhatsappPending(true);
      setAuthIntent("signup");
      setAuthModalOpen(true);
      return;
    }

    if (isTopicContentAccessGranted(isAuthenticated, user, topicIds)) {
      openExpertWhatsapp();
      return;
    }

    setWhatsappPending(true);
    setInterestGateOpen(true);
  }, [
    expertWhatsappUrl,
    isAuthenticated,
    user,
    topicIds,
    openExpertWhatsapp,
    setInterestGateOpen,
  ]);

  useEffect(() => {
    if (!isAuthenticated || !expertWhatsappUrl) return;
    try {
      const key = `ibx-pending-whatsapp:${location.pathname}`;
      if (!sessionStorage.getItem(key)) return;
      sessionStorage.removeItem(key);
      setWhatsappPending(true);
    } catch {
      /* ignore */
    }
  }, [isAuthenticated, expertWhatsappUrl, location.pathname]);

  useEffect(() => {
    if (!whatsappPending || !isAuthenticated || !user) return;

    if (isTopicContentAccessGranted(isAuthenticated, user, topicIds)) {
      openExpertWhatsapp();
      setWhatsappPending(false);
      return;
    }

    if (!interestGateOpen) {
      setInterestGateOpen(true);
    }
  }, [
    whatsappPending,
    isAuthenticated,
    user,
    topicIds,
    interestGateOpen,
    openExpertWhatsapp,
    setInterestGateOpen,
  ]);

  useEffect(() => {
    const wasOpen = interestGateWasOpenRef.current;
    interestGateWasOpenRef.current = interestGateOpen;

    if (wasOpen && !interestGateOpen && whatsappPending) {
      if (isTopicContentAccessGranted(isAuthenticated, user, topicIds)) {
        openExpertWhatsapp();
      }
      setWhatsappPending(false);
    }
  }, [
    interestGateOpen,
    whatsappPending,
    isAuthenticated,
    user,
    topicIds,
    openExpertWhatsapp,
  ]);

  useEffect(() => {
    const tab = location.state?.activeTab;
    if (tab === "articles" || tab === "media" || tab === "overview") {
      setActiveTab(tab);
    }
  }, [location.state?.activeTab]);

  useEffect(() => {
    if (activeTab !== "articles") return;
    scrollToTopicContent();
  }, [activeTab, scrollToTopicContent]);

  const summaries = SUMMARIES_BY_INDUSTRY[industryId] ?? {};
  const summary = summaries[menuItem?.id] ?? null;

  useEffect(() => {
    const el = asideStickyRef.current;
    if (!el || typeof window === "undefined") return undefined;

    let ticking = false;
    const evaluate = () => {
      ticking = false;
      const node = asideStickyRef.current;
      if (!node) return;
      const top = node.getBoundingClientRect().top;
      const scrollEnough = window.scrollY > 120;
      /** Sticky has “caught”; allow small subpixel jitter from layout. */
      const pinnedNow = scrollEnough && top <= ASIDE_STICKY_TOP_PX + 8;
      setRailPinned(pinnedNow);
    };

    const onScrollResize = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(evaluate);
      }
    };

    window.addEventListener("scroll", onScrollResize, { passive: true });
    window.addEventListener("resize", onScrollResize, { passive: true });
    evaluate();
    return () => {
      window.removeEventListener("scroll", onScrollResize);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [slug, industry, menuItem]);

  if (categoriesLoading && (!industry || !menuItem)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f4ec] px-4">
        <div
          className="flex max-w-sm flex-col items-center gap-4 rounded-3xl border border-black/[0.06] bg-white p-10 text-center shadow-lg"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <Loader2 className="h-10 w-10 shrink-0 animate-spin text-[#c9a000]" aria-hidden />
          <div>
            <p className="text-base font-black text-gray-900">Loading topic</p>
            <p className="mt-1 text-sm text-gray-500">Fetching categories and menu…</p>
          </div>
        </div>
      </div>
    );
  }

  if (!industry || !menuItem) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f7f4ec]">
        <div className="rounded-3xl bg-white p-10 text-center shadow-xl">
          <p className="mb-3 text-4xl font-black text-gray-900">Topic not found</p>
          <Link to={`/${industryId}`} className="font-semibold text-[#c9a000] underline">
            Browse {industry?.label ?? "all topics"}
          </Link>
        </div>
      </div>
    );
  }

  const color = menuItem.color ?? industry.color;
  const HeroIcon = importIcon(menuItem.icon);
  const sponsor = sponsorForContact;

  // Overview tab always uses static mock data (articles/media tabs use the API).
  const overview = buildSubcategoryOverview({
    industryId,
    slug,
    menuItem,
    summary,
  });
  const isRich = overview.mode === "rich";
  const richData = isRich ? overview.data : null;
  const OverviewView = isRich ? overview.OverviewView : null;
  const heroImage = overview.heroImage || "";
  const displayStats = overview.stats;
  /** Tab badges reflect API content scoped to this subcategory (not static mock totals). */
  const articleTabCount =
    canReadTopicContent && topicArticles.topicIds ? topicArticles.totalCount : null;
  const mediaTabItemCount =
    canReadTopicContent && topicMedia.canAccess && topicMedia.topicIds
      ? topicMedia.totalCount
      : null;

  const legalHubVisual = industryId === "legal" ? getLegalHubVisuals(slug ?? "") : null;
  const pageWash = legalHubVisual?.pageBg ?? "bg-[#f7f4ec]";
  const ambient = legalHubVisual?.ambientClass ?? "";

  return (
    <LegalHubVisualContext.Provider value={legalHubVisual}>
      <div className={`relative isolate min-h-screen ${pageWash} ${ambient}`}>
      <section className="relative overflow-hidden bg-black pt-28 text-white">
        {/* Texture */}
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.045]"
          aria-hidden
          style={{
            backgroundImage: `linear-gradient(to right, rgba(255,255,255,0.06) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255,255,255,0.06) 1px, transparent 1px)`,
            backgroundSize: "32px 32px",
          }}
        />
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-[0.55] sm:opacity-[0.6]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-slate-900/48 via-slate-900/28 to-slate-950/75" aria-hidden />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/88 via-transparent to-slate-900/38" aria-hidden />
        <div
          className="pointer-events-none absolute top-24 right-[-5rem] h-[22rem] w-[22rem] rounded-full blur-3xl sm:h-[26rem] sm:w-[26rem]"
          style={{ background: color, opacity: 0.14 }}
          aria-hidden
        />
        <div
          className="pointer-events-none absolute bottom-0 left-[-4rem] h-56 w-56 rounded-full blur-3xl"
          style={{ background: color, opacity: 0.18 }}
          aria-hidden
        />

        <div className="relative z-10 mx-auto max-w-8xl px-4 pb-0 sm:px-6 lg:px-8">
          <div className="mb-7 flex flex-wrap items-center gap-2 text-xs text-white/55">
            <Link to="/" className="transition hover:text-white">
              Home
            </Link>
            <ChevronRight size={11} />
            <Link to={`/${industryId}`} className="transition hover:text-white">
              {industry.label}
            </Link>
            <ChevronRight size={11} />
            <span className="text-white/90">{menuItem.label}</span>
          </div>

          <div className="grid gap-10 pb-12 lg:grid-cols-[1fr_360px] lg:items-end">
            <motion.div initial="hidden" animate="visible">
              <motion.div
                variants={fadeUp}
                className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] backdrop-blur"
              >
                <HeroIcon size={13} style={{ color }} />
                {industry.label}
              </motion.div>
              <motion.h1 variants={fadeUp} custom={0.04} className="max-w-4xl text-4xl font-black tracking-tight sm:text-[2.65rem] sm:leading-[1.06] lg:text-[3.25rem]">
                {menuItem.label}
              </motion.h1>
              <motion.p variants={fadeUp} custom={0.08} className="mt-5 max-w-2xl text-base leading-relaxed text-white/[0.93] drop-shadow-[0_1px_28px_rgba(0,0,0,0.35)] sm:text-lg sm:leading-8">
                {menuItem.description}
              </motion.p>
              {isRich && richData?.funnelTagline ? (
                <motion.p
                  variants={fadeUp}
                  custom={0.09}
                  className="mt-5 inline-flex max-w-full items-start gap-2 rounded-2xl border border-white/15 bg-white/[0.06] px-4 py-3 text-sm leading-relaxed text-white/88 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08)] backdrop-blur-md sm:text-[0.9375rem]"
                >
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full shadow-[0_0_12px]" style={{ background: color, boxShadow: `0 0 14px ${color}99` }} aria-hidden />
                  <span className="font-semibold [text-wrap:balance]">{richData.funnelTagline}</span>
                </motion.p>
              ) : null}
              <motion.div variants={fadeUp} custom={0.12} className="mt-8 flex flex-wrap gap-3">
                {isAuthenticated ? (
                  <button
                    type="button"
                    onClick={scrollToOverviewContent}
                    className="inline-flex items-center gap-2 rounded-2xl px-6 py-4 text-sm font-black shadow-lg transition hover:scale-[1.02] hover:brightness-110"
                    style={{ background: color, color: foregroundOnSolidAccent(color) }}
                  >
                    Get Started
                    <ArrowRight size={14} aria-hidden />
                  </button>
                ) : (
                  <Link
                    to={ctaTo}
                    className="inline-flex items-center gap-2 rounded-2xl px-6 py-4 text-sm font-black shadow-lg transition hover:scale-[1.02] hover:brightness-110"
                    style={{ background: color, color: foregroundOnSolidAccent(color) }}
                  >
                    Register Free
                    <ArrowRight size={14} aria-hidden />
                  </Link>
                )}
                <button
                  type="button"
                  onClick={handleTalkToExpert}
                  className="inline-flex items-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-6 py-4 text-sm font-bold text-white backdrop-blur transition hover:bg-white/15"
                >
                  <MessageCircle size={14} aria-hidden />
                  Talk to an expert
                </button>
              </motion.div>
            </motion.div>

            <motion.div variants={fadeUp} custom={0.16} initial="hidden" animate="visible">
              <ProviderHeroCard
                industryId={industryId}
                provider={topicProvider.provider}
                isAuthenticated={isAuthenticated}
                fallbackSponsor={sponsor}
                topicLabel={menuItem.label}
                color={color}
                onTalkToExpertClick={handleTalkToExpert}
              />
            </motion.div>
          </div>

          <div className="grid grid-cols-3 gap-px rounded-t-xl border border-white/[0.08] bg-white/[0.05] backdrop-blur-sm">
            {displayStats.length > 0 ? (
              displayStats.map((stat) => (
                <div
                  key={stat.label}
                  className="relative px-4 py-5 sm:px-8 sm:py-6"
                >
                  <div
                    className="pointer-events-none absolute inset-x-3 top-3 h-[3px] rounded-full opacity-80 sm:inset-x-5"
                    style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
                    aria-hidden
                  />
                  <p className="text-2xl font-black tracking-tight sm:text-3xl" style={{ color: heroAccentStatColor(color) }}>
                    {stat.value}
                  </p>
                  <p className="mt-2 text-[10px] font-bold uppercase tracking-[0.18em] text-white/42 sm:tracking-[0.2em]">{stat.label}</p>
                </div>
              ))
            ) : (
              <div className="col-span-3 px-4 py-5 text-center text-xs font-semibold text-white/50 sm:py-6">
                Expert-backed guides for this topic
              </div>
            )}
          </div>
        </div>
      </section>

      <StickyTabBar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        color={color}
        articleCount={articleTabCount > 0 ? String(articleTabCount) : null}
        mediaCount={mediaTabItemCount > 0 ? String(mediaTabItemCount) : null}
        hubVisual={legalHubVisual}
      />

      <div
        id="topic-content"
        className={`scroll-mt-[7.5rem] mx-auto max-w-8xl px-4 py-10 sm:px-6 sm:scroll-mt-[8.5rem] lg:grid lg:grid-cols-[1fr_340px] lg:px-8 ${legalHubVisual ? "lg:gap-x-10 lg:gap-y-10 lg:py-12" : "lg:gap-8"}`}
      >
        <main className="min-w-0">
          <AnimatePresence mode="wait">
            {activeTab === "overview" && (
              <motion.div key="overview" {...tabFade}>
                {isRich && OverviewView ? (
                  <OverviewView data={richData} color={color} />
                ) : (
                  <GenericOverviewTab
                    experience={overview.experience}
                    summary={overview.summary}
                    color={color}
                  />
                )}
              </motion.div>
            )}

            {activeTab === "articles" && (
              <motion.div key="articles" {...tabFade}>
                <TopicArticlesTab
                  color={color}
                  industryId={industryId}
                  slug={slug}
                  topicLabel={menuItem.label}
                  articlesState={topicArticles}
                  canReadFull={canReadTopicContent}
                  isAuthenticated={isAuthenticated}
                  ctaTo={ctaTo}
                  onOpenSignup={openSignupModal}
                  onRequestInterest={() => interestGate.setOpen(true)}
                />
              </motion.div>
            )}

            {activeTab === "media" && (
              <motion.div key="media" {...tabFade}>
                <TopicMediaTab
                  color={color}
                  ctaTo={ctaTo}
                  isAuthenticated={isAuthenticated}
                  topicLabel={menuItem.label}
                  onRequestInterest={() => interestGate.setOpen(true)}
                  onOpenSignup={openSignupModal}
                  media={topicMedia}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </main>

        <aside
          ref={asideStickyRef}
          className={`relative mt-10 flex shrink-0 flex-col lg:sticky lg:top-[calc(4.375rem+3.875rem)] lg:mt-0 lg:self-start lg:pb-6 ${legalHubVisual ? "gap-5" : "gap-4"} ${railPinned && legalHubVisual ? "lg:rounded-[2rem] lg:border lg:border-slate-200/70 lg:bg-white/[0.48] lg:px-4 lg:py-5 lg:shadow-[0_32px_85px_-48px_rgba(15,23,42,0.55)] lg:backdrop-blur-2xl lg:ring-1 lg:ring-white/80" : ""} ${railPinned && !legalHubVisual ? "lg:rounded-2xl lg:shadow-[0_24px_55px_-40px_rgba(15,23,42,0.22)]" : ""}`}
        >
          {railPinned && legalHubVisual ? (
            <div
              className="pointer-events-none absolute top-[9%] bottom-[9%] left-0 z-[1] hidden w-[3px] rounded-full opacity-90 lg:block"
              style={{
                background: `linear-gradient(180deg, transparent 0%, ${color} 45%, ${color} 55%, transparent 100%)`,
                boxShadow: `0 0 22px ${color}55`,
              }}
              aria-hidden
            />
          ) : null}
          <div
            className={`shrink-0 transition-[margin-top] duration-300 ease-out ${railPinned ? "lg:mt-5" : "lg:mt-0"}`}
          >
            <ProviderSidebarCard
              industryId={industryId}
              provider={topicProvider.provider}
              isAuthenticated={isAuthenticated}
              fallbackSponsor={sponsor}
              topicLabel={menuItem.label}
              color={color}
              onTalkToExpertClick={handleTalkToExpert}
            />
          </div>

          <div
            className={`rounded-[2rem] border border-black/[0.07] bg-white p-6 ${legalHubVisual ? "shadow-[0_14px_40px_-28px_rgba(15,23,42,0.18)] ring-1 ring-slate-200/50" : "shadow-sm"}`}
          >
            <p className="mb-4 text-xs font-black uppercase tracking-[0.2em] text-gray-400">More in {industry.label}</p>
            <div className="space-y-0.5">
              {(Array.isArray(menuCategories) ? menuCategories : [])
                .filter((c) => c.id !== menuItem.id)
                .slice(0, 6)
                .map((c) => {
                  return (
                    <Link
                      key={c.id}
                      to={c.path}
                      className="flex items-center justify-between gap-2 rounded-xl px-3 py-3 text-sm font-semibold text-gray-600 transition hover:bg-[#f7f4ec] hover:text-black"
                    >
                      <span>{c.label}</span>
                      <ChevronRight size={13} className="text-gray-300" />
                    </Link>
                  );
                })}
            </div>
            <Link
              to={industry?.path || `/${industryId}`}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-black/10 py-3 text-xs font-black text-black transition hover:bg-[#FFD221]"
            >
              View all topics
              <ArrowRight size={12} />
            </Link>
          </div>
        </aside>
      </div>

      <Footer />

      <ArticleAuthFlowModal
        open={authModalOpen}
        onClose={() => {
          guestPromptConsumedRef.current = true;
          setAuthModalOpen(false);
          if (!isAuthenticated) setWhatsappPending(false);
        }}
        onAuthenticated={() => setAuthModalOpen(false)}
        openWith={authIntent}
        articleTitle={menuItem?.label || "this topic"}
        sponsorName={sponsor?.name || "your provider"}
        returnPath={`${location.pathname}${location.search || ""}`}
        articleInterestCategory={
          topicIds?.categoryName && topicIds?.subCategoryName ? topicIds.categoryName : ""
        }
        articleInterestSubCategory={
          topicIds?.categoryName && topicIds?.subCategoryName ? topicIds.subCategoryName : ""
        }
      />

      <SubcategoryInterestGateModal
        open={interestGate.open}
        color={interestGateAccent}
        industryLabel={interestGate.industryLabel}
        topicLabel={interestGate.topicLabel}
        onProceed={interestGate.onProceed}
        onGoBack={interestGate.onGoBack}
        pending={interestGate.pending}
      />
    </div>
    </LegalHubVisualContext.Provider>
  );
};

export default CategoryDetailPage;
